import React, { useEffect, useState } from "react";
import {
    Box, Typography, Paper, Table, TableBody, TableCell, 
    TableContainer, TableHead, TableRow, Button, TextField, 
    Stack, Chip, Dialog, DialogTitle, DialogContent, 
    DialogActions, MenuItem, FormControlLabel, Switch, Tooltip
} from "@mui/material";
import { Search, CheckCircle } from "@mui/icons-material";
import { collection, getDocs, doc, query, where, writeBatch } from "firebase/firestore";
import { db } from "../../firebase";
import { format } from "date-fns";

function Monthlypayments() {
    const [staffList, setStaffList] = useState([]);
    const [attendanceData, setAttendanceData] = useState([]);
    const [advanceData, setAdvanceData] = useState([]);
    const [paidRecords, setPaidRecords] = useState({}); // Stores full payment objects per staff ID
    const [loading, setLoading] = useState(false);
    
    const [selectedMonth, setSelectedMonth] = useState(format(new Date(), "yyyy-MM"));
    const [searchTerm, setSearchTerm] = useState("");
    
    // Dialog States
    const [payDialogOpen, setPayDialogOpen] = useState(false);
    const [activeStaff, setActiveStaff] = useState(null);
    const [deductAdvance, setDeductAdvance] = useState(true);
    const [paymentDetails, setPaymentDetails] = useState({
        date: format(new Date(), "yyyy-MM-dd"),
        amount: 0,
        payMode: "UPI",
        description: ""
    });

    useEffect(() => { 
        loadData(); 
    }, [selectedMonth]);

   const loadData = async () => {
    setLoading(true);
    try {
        // 1. Create targeted server queries instead of downloading entire tables
        const attendanceQuery = query(
            collection(db, "staffattendance"),
            where("date", ">=", `${selectedMonth}-01`),
            where("date", "<=", `${selectedMonth}-31`)
        );

        // 2. Fetch all components in parallel parallel execution
        const [staffSnap, attendanceSnap, advanceSnap, paymentsSnap] = await Promise.all([
            getDocs(collection(db, "salaryAllocations")),
            getDocs(attendanceQuery), // Targeted query
            getDocs(query(collection(db, "staffadvances"), where("status", "==", "Pending"))),
            getDocs(query(collection(db, "monthlyPayments"), where("month", "==", selectedMonth)))
        ]);

        // 3. Process maps onto local state securely
        setStaffList(staffSnap.docs.map(d => ({ id: d.id, ...d.data() })));
        setAttendanceData(attendanceSnap.docs.map(d => d.data())); // No client-side .filter() needed!
        setAdvanceData(advanceSnap.docs.map(d => ({ id: d.id, ...d.data() })));

        const paidMap = {};
        paymentsSnap.forEach(doc => { paidMap[doc.data().staffId] = doc.data(); });
        setPaidRecords(paidMap);

    } catch (e) {
        console.error("Data pipeline processing failure:", e);
    }
    setLoading(false);
};

    const calculateStats = (staff) => {
        // Evaluate contextual attendance structures via primary collection identifiers
        const logs = attendanceData.filter(a => a.staffId === staff.id);
        const p = logs.filter(l => l.status === "Present" || l.status === "Late Mark").length;
        const h = logs.filter(l => l.status === "Half Day").length;
        
        // Sum Pending structural balances tied to this user identity mapping
        const pendingAdvanceSum = advanceData
            .filter(adv => adv.staffId === staff.id)
            .reduce((sum, item) => sum + (Number(item.payment) || 0), 0);
        
        const base = Number(staff.totalSalary) || 0;
        const earned = Math.round((p * (base / 30)) + (h * 0.5 * (base / 30)));
        
        return { p, h, earned, pendingAdvanceSum };
    };

    const handleFinalPay = async () => {
        if (!activeStaff) return;

        const batch = writeBatch(db);
        const paymentId = `${activeStaff.id}_${selectedMonth}`;

        // 1. Commit Core Payroll Record Object
        const payRef = doc(db, "monthlyPayments", paymentId);
        batch.set(payRef, {
            staffId: activeStaff.id,
            staffName: activeStaff.staffName,
            month: selectedMonth,
            ...paymentDetails,
            amount: Number(paymentDetails.amount),
            advanceDeducted: deductAdvance,
            createdAt: new Date().toISOString()
        });

        // 2. Clear Active Status Flags on Advances if Deduction Mode is Enabled
        if (deductAdvance) {
            const staffAdvances = advanceData.filter(a => a.staffId === activeStaff.id);
            staffAdvances.forEach(adv => {
                const advRef = doc(db, "staffadvances", adv.id);
                batch.update(advRef, { status: "Paid", deductionMonth: selectedMonth });
            });
        }

        try {
            await batch.commit();
            setPayDialogOpen(false);
            loadData();
        } catch (error) {
            console.error("Failed to commit payroll batch execution sequence: ", error);
            alert("Database write execution failed. Check system structural credentials.");
        }
    };

    return (
        <Box p={3} sx={{ bgcolor: '#f4f6f8', minHeight: '100vh' }}>
            {/* Header / Filter Module Controls */}
            <Paper sx={{ p: 2.5, mb: 3, borderRadius: 3, boxShadow: '0 4px 12px rgba(0,0,0,0.03)' }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems="center" spacing={2}>
                    <Box>
                        <Typography variant="h6" fontWeight="800" color="primary.main">Monthly Payroll Processing</Typography>
                        <Typography variant="caption" color="textSecondary">Calculate earned baseline assets and execute transaction states</Typography>
                    </Box>
                    <Stack direction="row" spacing={2} sx={{ width: { xs: '100%', sm: 'auto' } }}>
                        <TextField 
                            type="month" 
                            size="small" 
                            value={selectedMonth} 
                            onChange={(e) => setSelectedMonth(e.target.value)}
                            label="Payroll Month"
                            InputLabelProps={{ shrink: true }}
                            sx={{ bgcolor: '#fff' }}
                        />
                        <TextField 
                            placeholder="Search staff profile name..." 
                            size="small" 
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            InputProps={{ startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} /> }}
                            sx={{ bgcolor: '#fff' }}
                        />
                    </Stack>
                </Stack>
            </Paper>

            {/* Main Application Matrix Table */}
            <TableContainer component={Paper} sx={{ borderRadius: 3, border: '1px solid #eee', boxShadow: 'none' }}>
                <Table size="small">
                    <TableHead sx={{ bgcolor: '#f8f9fa' }}>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 'bold', py: 1.5 }}>Staff Identity Parameters</TableCell>
                            <TableCell align="center" sx={{ fontWeight: 'bold', py: 1.5 }}>Attendance Profiles (P|H)</TableCell>
                            <TableCell align="center" sx={{ fontWeight: 'bold', py: 1.5 }}>Calculated Net Return</TableCell>
                            <TableCell align="center" sx={{ fontWeight: 'bold', py: 1.5 }}>Pending Advance Metrics</TableCell>
                            <TableCell align="center" sx={{ fontWeight: 'bold', py: 1.5 }}>Operational State</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 'bold', py: 1.5 }}>Allocation Processing</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell colSpan={6} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                                    Querying ledger collections...
                                </TableCell>
                            </TableRow>
                        ) : (
                            staffList
                                .filter(s => (s.staffName || "").toLowerCase().includes(searchTerm.toLowerCase()))
                                .map((staff) => {
                                    const { p, h, earned, pendingAdvanceSum } = calculateStats(staff);
                                    const paymentRecord = paidRecords[staff.id];
                                    const isPaid = !!paymentRecord;

                                    return (
                                        <TableRow key={staff.id} hover>
                                            <TableCell sx={{ py: 1.5 }}>
                                                <Typography variant="body2" fontWeight="bold">{staff.staffName || "Unresolved Persona"}</Typography>
                                                <Typography variant="caption" color="textSecondary">Base: ₹{(Number(staff.totalSalary) || 0).toLocaleString('en-IN')}</Typography>
                                            </TableCell>
                                            <TableCell align="center">
                                                <Chip label={`${p} Present | ${h} Half`} size="small" variant="outlined" color="primary" sx={{ borderRadius: 1.5 }} />
                                            </TableCell>
                                            <TableCell align="center" style={{ fontWeight: '600' }}>
                                                ₹{earned.toLocaleString('en-IN')}
                                            </TableCell>
                                            <TableCell align="center">
                                                <Typography color={pendingAdvanceSum > 0 ? "error.main" : "text.secondary"} fontWeight="bold" variant="body2">
                                                    ₹{pendingAdvanceSum.toLocaleString('en-IN')}
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="center">
                                                {isPaid ? (
                                                    <Tooltip title={`Cleared via ${paymentRecord.payMode} pipeline on ${paymentRecord.date}`}>
                                                        <Chip icon={<CheckCircle />} label="Settled" color="success" size="small" sx={{ fontWeight: 'bold', borderRadius: 1.5 }} />
                                                    </Tooltip>
                                                ) : (
                                                    <Chip label="Unprocessed" variant="outlined" size="small" sx={{ color: 'text.secondary', borderRadius: 1.5 }} />
                                                )}
                                            </TableCell>
                                            <TableCell align="right">
                                                {isPaid ? (
                                                    <Typography variant="body2" fontWeight="bold" color="success.main">
                                                        ₹{(paymentRecord.amount || 0).toLocaleString('en-IN')}
                                                    </Typography>
                                                ) : (
                                                    <Button 
                                                        variant="contained" 
                                                        size="small" 
                                                        sx={{ textTransform: 'none', borderRadius: 1.5, fontWeight: 'bold' }}
                                                        onClick={() => {
                                                            setActiveStaff(staff);
                                                            const net = deductAdvance ? (earned - pendingAdvanceSum) : earned;
                                                            setPaymentDetails({ ...paymentDetails, amount: net >= 0 ? net : 0 });
                                                            setPayDialogOpen(true);
                                                        }}
                                                    >
                                                        Run Payroll
                                                    </Button>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })
                        )}
                        {!loading && staffList.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={6} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                                    No functional entity items defined in system allocation configurations.
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Action Popup Dialog Sheet */}
            <Dialog open={payDialogOpen} onClose={() => setPayDialogOpen(false)} fullWidth maxWidth="xs" PaperProps={{ sx: { borderRadius: 3 } }}>
                <DialogTitle sx={{ fontWeight: 'bold', bgcolor: '#f8f9fa', borderBottom: '1px solid #eee' }}>
                    Authorize Ledger Release: {activeStaff?.staffName}
                </DialogTitle>
                <DialogContent sx={{ mt: 2 }}>
                    <Stack spacing={2.5}>
                        <Box display="flex" justifyContent="space-between" alignItems="center" p={1.5} bgcolor="#f0f7ff" borderRadius={2}>
                            <Typography variant="body2" fontWeight="500">Apply Advance Outstanding Deduction?</Typography>
                            <Switch 
                                checked={deductAdvance} 
                                size="small"
                                onChange={(e) => {
                                    const checked = e.target.checked;
                                    setDeductAdvance(checked);
                                    const { earned, pendingAdvanceSum } = calculateStats(activeStaff);
                                    const net = checked ? (earned - pendingAdvanceSum) : earned;
                                    setPaymentDetails({
                                        ...paymentDetails, 
                                        amount: net >= 0 ? net : 0
                                    });
                                }} 
                            />
                        </Box>

                        <TextField 
                            label="Target Liquid Disbursement (₹)" 
                            type="number" 
                            fullWidth 
                            size="small"
                            value={paymentDetails.amount} 
                            onChange={(e) => setPaymentDetails({...paymentDetails, amount: e.target.value})} 
                        />

                        <TextField 
                            type="date"
                            label="Execution Settlement Timestamp"
                            fullWidth
                            size="small"
                            InputLabelProps={{ shrink: true }}
                            value={paymentDetails.date}
                            onChange={(e) => setPaymentDetails({...paymentDetails, date: e.target.value})}
                        />

                        <TextField 
                            select 
                            label="Transfer Pipeline Protocol" 
                            fullWidth 
                            size="small" 
                            value={paymentDetails.payMode} 
                            onChange={(e) => setPaymentDetails({...paymentDetails, payMode: e.target.value})}
                        >
                            {["Cash", "UPI", "Bank Transfer"].map(m => (
                                <MenuItem key={m} value={m}>{m}</MenuItem>
                            ))}
                        </TextField>

                        <TextField 
                            label="Internal Processing Memo" 
                            placeholder="Add reference notes regarding monthly adjustments..."
                            fullWidth 
                            size="small"
                            multiline
                            rows={2}
                            value={paymentDetails.description || ""} 
                            onChange={(e) => setPaymentDetails({...paymentDetails, description: e.target.value})} 
                        />
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ p: 2, bgcolor: '#f8f9fa', borderTop: '1px solid #eee' }}>
                    <Button onClick={() => setPayDialogOpen(false)} color="inherit" sx={{ textTransform: 'none' }}>Cancel</Button>
                    <Button variant="contained" color="success" onClick={handleFinalPay} startIcon={<CheckCircle />} sx={{ textTransform: 'none', borderRadius: 2 }}>
                        Execute Release
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default Monthlypayments;